import LuxonUtils from "@js/utils/luxon-utils.es6.js";

export default class DateDisplayUtils {

  /**
   * Check if the provided date is an instance of a Date and throw an TypeError if not.
   * @param {Date} date the date to check
   * @throws {TypeError} if the provided date is not an instance of a Date
   * @return {void}
   */
  static #validateDate(date) {
    if (!(date instanceof Date)) {
      throw new TypeError('Date must be an instance of a Date.');
    }
  }

  /**
   * Format the provided date as the name of the weekday with the exact date in parenthesis.
   * For example: Monday (10/16)
   *
   * @param {Date} date the date to format
   * @throws {TypeError} if the provided date is not an instance of a Date
   * @return {string} the formatted date
   */
  static weekdayWithDate(date) {
    DateDisplayUtils.#validateDate(date);
    const weekdayLongName = LuxonUtils.getWeekdayLongName(date);
    const monthNumber = LuxonUtils.getMonthNumber(date);
    const dayNumber = LuxonUtils.getDayNumber(date);

    return `${weekdayLongName} (${monthNumber}/${dayNumber})`;
  }

  /**
   * Format the provided date as a short date.
   * For example: 7/19/24
   *
   * @param {Date} date the date to format
   * @throws {TypeError} if the provided date is not an instance of a Date
   * @return {string} the formatted date
   */
  static shortDate(date) {
    DateDisplayUtils.#validateDate(date);
    return LuxonUtils.getShortDateString(date);
  }

  /**
   * Format the provided date as a short time.
   * For example: 8:00am
   *
   * @param {Date} date the date to format
   * @throws {TypeError} if the provided date is not an instance of a Date
   * @return {string} the formatted date
   */
  static time(date) {
    DateDisplayUtils.#validateDate(date);
    const simpleTimeString = LuxonUtils.getSimpleTimeString(date);
    return simpleTimeString.replace(' ', '').toLowerCase();
  }

  /**
   * Format the provided date as a short time with offset.
   * For example: 8:00am EST
   *
   * @param {Date} date the date to format
   * @throws {TypeError} if the provided date is not an instance of a Date
   * @return {string} the formatted date
   */
  static timeWithOffset(date) {
    const time = DateDisplayUtils.time(date);
    const offsetName = LuxonUtils.getOffsetShortName(date);
    return `${time} ${offsetName}`;
  }

  /**
   * Format the provided start and end date as a range of hours.
   * For example: 8:00am - 2:00pm
   *
   * @param {Date} startDate the start date for the hours range to format
   * @param {Date} endDate the end date for the hours range to format
   * @throws {TypeError} if the provided startDate or endDate is not an instance of a Date
   * @throws {Error} if the provided endDate is not after provided startDate
   * @return {string} the formatted date range
   */
  static hoursRange(startDate, endDate) {
    DateDisplayUtils.#validateDate(startDate);
    DateDisplayUtils.#validateDate(endDate);

    if (startDate >= endDate) {
      throw new Error('EndDate must be after startDate.');
    } else if (startDate.Date !== endDate.Date) {
      throw new Error('StartDate and endDate must fall on the same day.');
    }

    const startTime = DateDisplayUtils.time(startDate);
    const endTime = DateDisplayUtils.time(endDate);
    const offsetName = LuxonUtils.getOffsetShortName(startDate);

    return `${startTime} - ${endTime} ${offsetName}`;
  }
}
